/**
 * Shared helpers for session forms.
 */
import { toLocalDateString } from '@/utils/format-date-utils';
import { addSessionParticipant } from '@/services/remote/sessions.service';
import { reserveWelcomeMeeting } from '@/services/remote/students.service';
import { reserveInformationSession } from '@/services/remote/tutors.service';
import {
  CANCELLATION_REASONS,
  REMOTE_ANIMATOR_SESSION_TYPES,
  REMOTE_FACILITATOR_SESSION_TYPES,
  SESSION_REMOTE_FORM_DEFAULTS,
  SESSION_REMOTE_FORM_ROLES,
} from '@/components/sessions/session-configs';
import {
  type CancellationReasonCode,
  type CreateSessionDto,
  type ParticipantChip,
  type SessionFormParticipant,
  type SessionFormRecord,
  type SessionTab,
} from '@/types/remote/session.types';
import type {
  BudgetPost,
  RecurrencePreview,
  UpdateSessionDto,
} from '@/types/inperson/session.types';
import type { SelectedUser } from '@/types/user.types';
import { getErrorMessage } from '@/utils/errors-utils';
import { roleKind } from '@/utils/role-label-utils';

export { isTerminalStatus } from '@/utils/common-utils';

/** Generic `{ value, label }` select option. */
export interface SelectOption {
  value: string;
  label: string;
}

/** Create vs edit session form. */
export type FormMode = 'create' | 'edit';

/** First+last, then email, then id. */
export function formatUserLabel(
  user:
    | {
        firstName?: string | null;
        lastName?: string | null;
        email?: string | null;
        id?: string;
      }
    | null
    | undefined,
): string {
  return `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || user?.email || user?.id || '';
}

/** Drop already-selected ids, then map each scoped row to a {@link Select} option. */
export function mapScopedUsersToParticipantOptions(
  rows: Array<{
    userId: string;
    user?: {
      firstName?: string | null;
      lastName?: string | null;
      email?: string | null;
    } | null;
  }>,
  selectedIds: string[],
): SelectOption[] {
  const selected = new Set(selectedIds);
  return rows
    .filter((row) => !selected.has(row.userId))
    .map((row) => mapScopedUserToOption(row.user, row.userId));
}

/** Map a scoped user + id to a {@link Select} option. */
export function mapScopedUserToOption(
  user:
    | {
        firstName?: string | null;
        lastName?: string | null;
        email?: string | null;
      }
    | null
    | undefined,
  userId: string,
): SelectOption {
  return {
    value: userId,
    label: formatUserLabel({ ...user, id: userId }),
  };
}

/** Keep a prefilled option in the list even if the catalog omitted it. */
export function withPrefillOption(
  base: SelectOption[],
  id: string | undefined,
  label: string | undefined,
): SelectOption[] {
  if (!id || !label || base.some((o) => o.value === id)) return base;
  return [{ value: id, label }, ...base];
}

/** Keep an existing chip's option in the select. */
export function withExistingChipOption(
  base: SelectOption[],
  chip: { id: string; name: string } | null,
): SelectOption[] {
  if (!chip || base.some((o) => o.value === chip.id)) return base;
  return [{ value: chip.id, label: chip.name }, ...base];
}

/** Keep the current user in managed-by options when they own the session. */
export function buildManagedByOptions(
  managedByRawOptions: SelectOption[],
  managedById: string | null,
  currentUser:
    | {
        id: string;
        firstName?: string | null;
        lastName?: string | null;
        email?: string | null;
      }
    | null
    | undefined,
): SelectOption[] {
  if (!managedById || managedById !== currentUser?.id) return managedByRawOptions;
  if (managedByRawOptions.some((o) => o.value === managedById)) return managedByRawOptions;
  return [{ value: currentUser.id, label: formatUserLabel(currentUser) }, ...managedByRawOptions];
}

/** Keep the stored manager in create-form options. */
export function createManagedByOptions(
  managedByOptions: SelectOption[],
  managedBy: { id: string; firstName?: string | null; lastName?: string | null } | null | undefined,
): SelectOption[] {
  if (!managedBy) return managedByOptions;
  if (managedByOptions.some((o) => o.value === managedBy.id)) return managedByOptions;
  const label = `${managedBy.firstName ?? ''} ${managedBy.lastName ?? ''}`.trim() || managedBy.id;
  return [{ value: managedBy.id, label }, ...managedByOptions];
}

/** True when `roles` includes `roleCode`. */
export function hasFormRole(roles: Array<{ code: string }>, roleCode: string): boolean {
  return roles.some((r) => r.code === roleCode);
}

/** True when the actor has the remote coordinator role. */
export function isCoordinatorRole(roles: Array<{ code: string }>): boolean {
  return hasFormRole(roles, SESSION_REMOTE_FORM_ROLES.coordinator);
}

/** True when the actor has the remote recruiter role. */
export function isRecruiterRole(roles: Array<{ code: string }>): boolean {
  return hasFormRole(roles, SESSION_REMOTE_FORM_ROLES.recruiter);
}

/** Animator role codes for a welcome vs information tab. */
export function animatorRoleCodesForTab(sessionType: SessionTab): string[] {
  return sessionType === 'welcome-meeting'
    ? [SESSION_REMOTE_FORM_ROLES.coordinator]
    : [SESSION_REMOTE_FORM_ROLES.recruiter];
}

/** Animator role codes for a welcome vs information session type. */
export function animatorRoleCodesForSessionType(sessionType: string): string[] {
  return sessionType === 'welcome_meeting'
    ? [SESSION_REMOTE_FORM_ROLES.coordinator]
    : [SESSION_REMOTE_FORM_ROLES.recruiter];
}

/** Coordinators default managed-by to themselves. */
export function defaultManagedById(
  isCoordinator: boolean,
  currentUserId: string | undefined,
): string | null {
  return isCoordinator && currentUserId ? currentUserId : null;
}

/** True for individual or group tutoring. */
export function isTutoringType(type: string | undefined): boolean {
  return type === 'individual_tutoring' || type === 'group_tutoring';
}

/** True when recurrence is set and not `none`/`once`. */
export function isRecurringRecurrenceType(recurrenceType: string | null | undefined): boolean {
  return Boolean(recurrenceType) && recurrenceType !== 'none' && recurrenceType !== 'once';
}

/** Recurring vs single from the effective recurrence type. */
export function resolveRecurrenceMode(effectiveRecurrenceType: string): 'recurring' | 'single' {
  return isRecurringRecurrenceType(effectiveRecurrenceType) ? 'recurring' : 'single';
}

/** True if tab is a remote meeting (RI or RA). */
export function isRemoteMeetingTab(tab: SessionTab): boolean {
  return tab === 'information-meeting' || tab === 'welcome-meeting';
}

/** Build recurrence fields for meeting create (RI/RA): mode, recurrenceType, recurrenceRule. */
export function buildMeetingRecurrenceFields(params: {
  uiType: string;
  sessionDate: string;
  customDates?: string[];
}): {
  mode: 'recurring' | 'single';
  recurrenceType: string;
  recurrenceRule?: Record<string, unknown> | null;
} {
  const { uiType, sessionDate, customDates } = params;

  if (!uiType || uiType === 'none' || uiType === 'once') {
    return { mode: 'single', recurrenceType: 'none', recurrenceRule: null };
  }

  if (uiType === 'daily' || uiType === 'weekly' || uiType === 'biweekly' || uiType === 'monthly') {
    return { mode: 'recurring', recurrenceType: uiType, recurrenceRule: null };
  }

  if (uiType === 'yearly') {
    return {
      mode: 'recurring',
      recurrenceType: 'custom',
      recurrenceRule: { interval: 1, intervalUnit: 'years' },
    };
  }

  if (uiType === 'custom') {
    const allDates = [sessionDate, ...(customDates ?? [])];
    const uniqueDates = Array.from(new Set(allDates)).sort((a, b) => a.localeCompare(b));
    return {
      mode: 'recurring',
      recurrenceType: 'custom',
      recurrenceRule: { dates: uniqueDates },
    };
  }

  return { mode: 'single', recurrenceType: 'once', recurrenceRule: null };
}

/** Args that decide if a meeting create form may fetch a recurrence preview. */
export type MeetingRecurrencePreviewEnabledParams = {
  sessionDate: string;
  uiType: string;
  customDates?: string[];
};

/** True when the meeting create form has enough recurrence fields to preview. */
export function isMeetingRecurrencePreviewEnabled(
  params: MeetingRecurrencePreviewEnabledParams,
): boolean {
  if (!params.sessionDate || !isRecurringRecurrenceType(params.uiType)) return false;
  if (params.uiType === 'custom') return (params.customDates?.length ?? 0) > 0;
  return true;
}

/** Count + planning-end sentence for the RI/RA create preview, or null when empty. */
export function formatMeetingRecurrencePreview(
  preview:
    | (Pick<RecurrencePreview, 'count'> &
        Partial<Pick<RecurrencePreview, 'planningEndDate' | 'schoolYearEndDate'>>)
    | null
    | undefined,
): string | null {
  if (!preview?.count) return null;
  const end = preview.planningEndDate ?? preview.schoolYearEndDate;
  if (end) return `Environ ${preview.count} réunions jusqu'au ${end}.`;
  return `Environ ${preview.count} réunions.`;
}

/** Today's date as local `YYYY-MM-DD`. */
export function todayDateString(): string {
  const t = new Date();
  return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
}

/** Default end time = start + 1h. */
export function addOneHour(time: string): string {
  const [h, m] = time.split(':').map(Number);
  const total = h * 60 + m + 60;
  return `${String(Math.floor(total / 60) % 24).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}

/** Today's start-time floor so past slots cannot be picked. */
export function getMinTimeForDate(sessionDate: string, todayStr: string): string | undefined {
  if (!sessionDate) return undefined;
  if (sessionDate === todayStr) {
    const now = new Date();
    return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  }
  return undefined;
}

/** Inclusive end date from start + `eventDays`. */
export function calculateEventEndDate(sessionDate: string, eventDays: number): string {
  if (!sessionDate || !eventDays) return '';
  const [y, m, d] = sessionDate.split('-').map(Number);
  const start = new Date(y, m - 1, d);
  start.setDate(start.getDate() + eventDays - 1);
  return `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}-${String(start.getDate()).padStart(2, '0')}`;
}

/** Welcome meetings count as 1 ETP when a single animator is set. */
export function getCreateEtpCount(
  sessionType: SessionTab,
  singleAnimatorId: string,
  selectedAnimatorsCount: number,
): number {
  if (sessionType === 'welcome-meeting') {
    return singleAnimatorId ? 1 : 0;
  }
  return selectedAnimatorsCount;
}

/** Same ETP rule as create, using API session-type keys. */
export function getEditEtpCount(
  sessionType: string,
  singleAnimatorId: string,
  selectedAnimatorsCount: number,
): number {
  if (sessionType === 'welcome_meeting') {
    return singleAnimatorId ? 1 : 0;
  }
  return selectedAnimatorsCount;
}

/** Sum of budget-post amounts. */
export function sumBudgetPosts(budgetPosts: BudgetPost[]): number {
  return budgetPosts.reduce((sum, p) => sum + (p.amount || 0), 0);
}

/** Posts total + ETP total + grand total. */
export function computeBudgetTotals(
  budgetPosts: BudgetPost[],
  etpCount: number,
  etpUnitCost: number,
) {
  const budgetPostsTotal = sumBudgetPosts(budgetPosts);
  const etpTotal = etpCount * etpUnitCost;
  return {
    budgetPostsTotal,
    etpTotal,
    budgetGrandTotal: budgetPostsTotal + etpTotal,
  };
}

/** Tutor ids for create: one on tutoring, many on information meetings. */
export function resolveTutorIds(
  sessionType: SessionTab,
  tutorId: string,
  selectedTutorParticipants: SelectedUser[],
): string[] | undefined {
  if (sessionType === 'tutoring') return [tutorId];
  if (sessionType === 'information-meeting') return selectedTutorParticipants.map((t) => t.id);
  return undefined;
}

/** Student ids for create: one on tutoring, many on welcome meetings. */
export function resolveStudentIds(
  sessionType: SessionTab,
  studentId: string,
  selectedStudentParticipants: SelectedUser[],
): string[] | undefined {
  if (sessionType === 'tutoring') return [studentId];
  if (sessionType === 'welcome-meeting') return selectedStudentParticipants.map((s) => s.id);
  return undefined;
}

/** Animator ids for create (undefined on tutoring). */
export function resolveAnimatorIds(
  sessionType: SessionTab,
  singleAnimatorId: string,
  selectedAnimators: SelectedUser[],
): string[] | undefined {
  if (sessionType === 'tutoring') return undefined;
  if (sessionType === 'welcome-meeting') {
    return singleAnimatorId ? [singleAnimatorId] : [];
  }
  return selectedAnimators.map((a) => a.id);
}

/** Queue tutor add + information-session reserve after creating a meeting. */
function pushTutorParticipantUpdates(
  updates: Promise<unknown>[],
  sessionId: string,
  companyId: string,
  tutors: SelectedUser[],
) {
  for (const tutor of tutors) {
    updates.push(
      addSessionParticipant(sessionId, {
        userId: tutor.id,
        role: 'volunteer',
        attendanceStatus: 'pending',
      })
        .then(() => reserveInformationSession(tutor.id, companyId, sessionId))
        .catch((err) => console.error(`Failed to register tutor ${tutor.id}:`, err)),
    );
  }
}

/** Queue welcome-meeting reserves for selected students after create. */
function pushStudentParticipantUpdates(
  updates: Promise<unknown>[],
  sessionId: string,
  companyId: string,
  students: SelectedUser[],
) {
  for (const student of students) {
    updates.push(
      reserveWelcomeMeeting(student.id, companyId, sessionId).catch((err) =>
        console.error(`Failed to register student ${student.id}:`, err),
      ),
    );
  }
}

/** After create: register tutors / students / animators on the new session. */
export async function registerPostCreateParticipants(params: {
  sessionType: SessionTab;
  sessionId: string;
  companyId: string;
  selectedTutorParticipants: SelectedUser[];
  selectedStudentParticipants: SelectedUser[];
}): Promise<void> {
  const {
    sessionType,
    sessionId,
    companyId,
    selectedTutorParticipants,
    selectedStudentParticipants,
  } = params;
  const updates: Promise<unknown>[] = [];

  if (sessionType === 'information-meeting' && selectedTutorParticipants.length > 0) {
    pushTutorParticipantUpdates(updates, sessionId, companyId, selectedTutorParticipants);
  }
  if (sessionType === 'welcome-meeting' && selectedStudentParticipants.length > 0) {
    pushStudentParticipantUpdates(updates, sessionId, companyId, selectedStudentParticipants);
  }

  await Promise.allSettled(updates);
}

/** Field-event budget payload, or undefined for other session types. */
function buildFieldEventBudget(
  sessionType: SessionTab,
  budgetPosts: BudgetPost[],
  etpCount: number,
  etpUnitCost: number,
  budgetGrandTotal: number,
) {
  if (sessionType !== 'field-event') return undefined;
  return {
    posts: budgetPosts,
    etp: etpCount,
    unitCost: etpUnitCost,
    grandTotal: budgetGrandTotal,
  };
}

/** Build the create-session DTO from form state. */
export function buildCreateSessionDto(params: {
  mapping: {
    type: CreateSessionDto['type'];
    sessionFormat: CreateSessionDto['sessionFormat'];
  };
  sessionType: SessionTab;
  sessionDate: string;
  startTime: string;
  endTime: string;
  recurrenceType: string;
  planDuringHolidays: boolean;
  tutorId: string;
  studentId: string;
  selectedTutorParticipants: SelectedUser[];
  selectedStudentParticipants: SelectedUser[];
  singleAnimatorId: string;
  selectedAnimators: SelectedUser[];
  managedById: string | null;
  maxParticipants: number;
  internalNotes: string;
  eventType: string;
  location: string;
  budgetPosts: BudgetPost[];
  etpCount: number;
  etpUnitCost: number;
  budgetGrandTotal: number;
  programId: string;
  customDates?: string[];
}): CreateSessionDto {
  const { mapping, sessionType } = params;

  let mode: 'recurring' | 'single';
  let effectiveRecurrenceType: string;
  let recurrenceRule: Record<string, unknown> | null | undefined;

  if (isRemoteMeetingTab(sessionType)) {
    const recurrenceFields = buildMeetingRecurrenceFields({
      uiType: params.recurrenceType,
      sessionDate: params.sessionDate,
      customDates: params.customDates,
    });
    mode = recurrenceFields.mode;
    effectiveRecurrenceType = recurrenceFields.recurrenceType;
    recurrenceRule = recurrenceFields.recurrenceRule;
  } else {
    effectiveRecurrenceType = sessionType === 'tutoring' ? params.recurrenceType : 'once';
    mode = resolveRecurrenceMode(effectiveRecurrenceType);
    recurrenceRule = undefined;
  }

  return {
    type: mapping.type,
    sessionFormat: mapping.sessionFormat,
    sessionDate: params.sessionDate,
    startTime: params.startTime,
    endTime: params.endTime || undefined,
    mode,
    recurrenceType: effectiveRecurrenceType,
    recurrenceRule,
    planDuringHolidays: sessionType === 'tutoring' ? params.planDuringHolidays : undefined,
    tutorIds: resolveTutorIds(sessionType, params.tutorId, params.selectedTutorParticipants),
    studentIds: resolveStudentIds(
      sessionType,
      params.studentId,
      params.selectedStudentParticipants,
    ),
    animatorIds: resolveAnimatorIds(sessionType, params.singleAnimatorId, params.selectedAnimators),
    managedById: sessionType === 'tutoring' ? params.managedById : undefined,
    maxStudents: sessionType === 'information-meeting' ? params.maxParticipants : undefined,
    internalNotes: params.internalNotes || undefined,
    eventType: sessionType === 'field-event' ? params.eventType : undefined,
    location: sessionType === 'field-event' ? params.location : undefined,
    budget: buildFieldEventBudget(
      sessionType,
      params.budgetPosts,
      params.etpCount,
      params.etpUnitCost,
      params.budgetGrandTotal,
    ),
    programId: params.programId || undefined,
  };
}

/** Tutor ids on edit: chips + leftover GET participants. */
export function computeTutorIds(
  isTutoring: boolean,
  isInformationMeeting: boolean,
  selectedTutor: ParticipantChip | null,
  selectedTutorParticipants: ParticipantChip[],
): string[] | undefined {
  if (isTutoring && selectedTutor) {
    return [selectedTutor.id];
  }
  if (isInformationMeeting) {
    return selectedTutorParticipants.map((t) => t.id);
  }
  return undefined;
}

/** Student ids on edit: chips + leftover GET participants. */
export function computeStudentIds(
  isTutoring: boolean,
  sessionType: string,
  selectedStudent: ParticipantChip | null,
  selectedStudentParticipants: ParticipantChip[],
): string[] | undefined {
  if (isTutoring) {
    return selectedStudent ? [selectedStudent.id] : [];
  }
  if (sessionType === 'welcome_meeting') {
    return selectedStudentParticipants.map((s) => s.id);
  }
  return undefined;
}

/** Animator ids on edit (undefined on tutoring). */
export function computeAnimatorIds(
  isTutoring: boolean,
  sessionType: string,
  singleAnimatorId: string,
  selectedAnimators: ParticipantChip[],
): string[] | undefined {
  if (isTutoring) return undefined;
  if (sessionType === 'welcome_meeting') {
    return singleAnimatorId ? [singleAnimatorId] : [];
  }
  return selectedAnimators.map((a) => a.id);
}

/** Combined tutor / student / animator ids for the update DTO. */
export function computeParticipantIds(params: {
  session: SessionFormRecord;
  isTutoring: boolean;
  selectedTutor: ParticipantChip | null;
  selectedStudent: ParticipantChip | null;
  selectedTutorParticipants: ParticipantChip[];
  selectedStudentParticipants: ParticipantChip[];
  selectedAnimators: ParticipantChip[];
  singleAnimatorId: string;
}) {
  return {
    tutorIds: computeTutorIds(
      params.isTutoring,
      params.session.type === 'information_meeting',
      params.selectedTutor,
      params.selectedTutorParticipants,
    ),
    studentIds: computeStudentIds(
      params.isTutoring,
      params.session.type,
      params.selectedStudent,
      params.selectedStudentParticipants,
    ),
    animatorIds: computeAnimatorIds(
      params.isTutoring,
      params.session.type,
      params.singleAnimatorId,
      params.selectedAnimators,
    ),
  };
}

/** API `creationSource` for sessions created via POST …/binomes/:id/sessions. */
export const SESSION_CREATION_SOURCE_PAIR_ONE_OFF = 'binome_one_off' as const;

/** True when the session was created as a one-off from an active pair. */
export function isPairOneOffSession(session: { creationSource?: string | null }): boolean {
  return session.creationSource === SESSION_CREATION_SOURCE_PAIR_ONE_OFF;
}

/** Title, subtitle, and default recurrence for the tutoring create form. */
export interface TutoringCreateFormChrome {
  title: string;
  subtitle: string;
  recurrenceType: string;
}

/** Flags that pick {@link TutoringCreateFormChrome}. */
export interface TutoringCreateFormChromeParams {
  isFromPair: boolean;
  isPrefilledFromMatching: boolean;
}

/** Resolve create-form chrome: pair one-off, matching prefill, or blank create. */
export function tutoringCreateFormChrome(
  params: TutoringCreateFormChromeParams,
): TutoringCreateFormChrome {
  if (params.isFromPair) {
    return {
      title: 'Ajouter une séance ponctuelle',
      subtitle: 'Une seule occurrence est ajoutée au binôme existant',
      recurrenceType: 'none',
    };
  }
  if (params.isPrefilledFromMatching) {
    return {
      title: 'Affectation du binôme',
      subtitle: 'Confirmer une affectation crée automatiquement le binôme correspondant',
      recurrenceType: 'weekly',
    };
  }
  return {
    title: 'Créer une séance de tutorat',
    subtitle: 'Renseignez les informations de la séance de tutorat',
    recurrenceType: 'none',
  };
}

/**
 * PATCH body for pair one-off edit: slot (+ holiday / notes) only.
 * Omits participants / program / recurrence so the backend lock accepts the request.
 */
export function buildPairOneOffUpdateDto(params: {
  sessionDate: string;
  startTime: string;
  endTime: string;
  planDuringHolidays: boolean;
  internalNotes: string;
}): UpdateSessionDto {
  return {
    sessionDate: params.sessionDate,
    startTime: params.startTime,
    endTime: params.endTime || undefined,
    planDuringHolidays: params.planDuringHolidays,
    internalNotes: params.internalNotes || undefined,
  };
}

export function buildUpdateDto(params: {
  session: SessionFormRecord;
  sessionDate: string;
  startTime: string;
  endTime: string;
  recurrenceType: string;
  planDuringHolidays: boolean;
  internalNotes: string;
  maxParticipants: number;
  tutorIds: string[] | undefined;
  studentIds: string[] | undefined;
  animatorIds: string[] | undefined;
  managedById: string | null;
  programId: string;
  eventType: string;
  location: string;
  budgetPosts: BudgetPost[];
  etpCount: number;
  etpUnitCost: number;
  budgetGrandTotal: number;
  updateFutureOccurrences: boolean;
}): UpdateSessionDto {
  const isTutoring = isTutoringType(params.session.type);
  const isFieldEvent = params.session.type === 'workshop';
  const recurrenceUpdateScope =
    params.session.recurrenceType &&
    params.session.recurrenceType !== 'none' &&
    params.updateFutureOccurrences
      ? 'following'
      : undefined;

  return {
    sessionDate: params.sessionDate,
    startTime: params.startTime,
    endTime: params.endTime || undefined,
    mode: isRecurringRecurrenceType(params.recurrenceType) ? 'recurring' : 'single',
    recurrenceType: params.recurrenceType as UpdateSessionDto['recurrenceType'],
    planDuringHolidays: params.planDuringHolidays,
    internalNotes: params.internalNotes || undefined,
    maxStudents: params.maxParticipants || undefined,
    tutorIds: params.tutorIds,
    studentIds: params.studentIds,
    animatorIds: params.animatorIds,
    managedById: isTutoring ? params.managedById : undefined,
    programId: isTutoring ? params.programId || undefined : undefined,
    eventType: isFieldEvent ? params.eventType : undefined,
    location: isFieldEvent ? params.location : undefined,
    budget: isFieldEvent
      ? {
          posts: params.budgetPosts,
          etp: params.etpCount,
          unitCost: params.etpUnitCost,
          grandTotal: params.budgetGrandTotal,
        }
      : undefined,
    recurrenceUpdateScope,
  };
}

/** User id from the nested user join, else the participant row. */
function resolveParticipantUserId(participant: SessionFormParticipant): string | null {
  return participant.user?.id ?? participant.userId ?? null;
}

/** Map a session participant to a form chip, or null without a user id. */
function participantToChip(
  participant: SessionFormParticipant,
  role: ParticipantChip['role'],
): ParticipantChip | null {
  const id = resolveParticipantUserId(participant);
  if (!id) return null;
  return {
    id,
    name: formatUserLabel(participant.user ?? { id }),
    role,
  };
}

/** Map participants to chips, dropping rows with no resolvable user id. */
function mapParticipantsToChips(
  participants: SessionFormParticipant[] | undefined,
  roleFor: (participant: SessionFormParticipant) => ParticipantChip['role'],
): ParticipantChip[] {
  if (!participants?.length) return [];
  const chips: ParticipantChip[] = [];
  for (const participant of participants) {
    const chip = participantToChip(participant, roleFor(participant));
    if (chip) chips.push(chip);
  }
  return chips;
}

/** Facilitators or animator-role participants used to hydrate form chips. */
function resolveFacilitatorSource(session: SessionFormRecord): SessionFormParticipant[] {
  let facilitatorSource = session.participants ?? [];
  if (REMOTE_FACILITATOR_SESSION_TYPES.has(session.type) && session.facilitators) {
    return session.facilitators;
  }
  if (!REMOTE_ANIMATOR_SESSION_TYPES.has(session.type)) {
    return [];
  }
  return facilitatorSource.filter((p) => {
    const kind = roleKind(p.role);
    return kind === 'recruiter' || kind === 'coordinator' || kind === 'volunteer';
  });
}

/** Hydrate create/edit form state from GET session + actor defaults. */
export function initializeFormState(
  session: SessionFormRecord,
  setters: {
    setSessionDate: (val: string) => void;
    setStartTime: (val: string) => void;
    setEndTime: (val: string) => void;
    setRecurrenceType: (val: string) => void;
    setPlanDuringHolidays: (val: boolean) => void;
    setInternalNotes: (val: string) => void;
    setMaxParticipants: (val: number) => void;
    setSelectedTutor: (val: ParticipantChip | null) => void;
    setSelectedStudent: (val: ParticipantChip | null) => void;
    setSingleAnimatorId: (val: string) => void;
    setSingleAnimatorName: (val: string) => void;
    setSelectedAnimators: (val: ParticipantChip[]) => void;
    setSelectedTutorParticipants: (val: ParticipantChip[]) => void;
    setSelectedStudentParticipants: (val: ParticipantChip[]) => void;
    setManagedById: (val: string | null) => void;
    setProgramId: (val: string) => void;
    setEventType: (val: string) => void;
    setLocation: (val: string) => void;
    setBudgetPosts: (val: BudgetPost[]) => void;
    setEtpUnitCost: (val: number) => void;
  },
) {
  setters.setSessionDate(toLocalDateString(new Date(session.sessionDate)));
  setters.setStartTime(session.startTime);
  setters.setEndTime(session.endTime || '');
  setters.setRecurrenceType(session.recurrenceType || '');
  setters.setPlanDuringHolidays(session.planDuringHolidays ?? false);
  setters.setInternalNotes(session.internalNotes || '');
  setters.setMaxParticipants(session.maxStudents || SESSION_REMOTE_FORM_DEFAULTS.maxParticipants);

  const tutor = session.participants?.find((p) => roleKind(p.role) === 'tutor');
  if (tutor?.user) {
    const chip = participantToChip(tutor, 'tutor');
    if (chip) setters.setSelectedTutor(chip);
  }

  const student = session.participants?.find((p) => roleKind(p.role) === 'student');
  if (student?.user) {
    const chip = participantToChip(student, 'student');
    if (chip) setters.setSelectedStudent(chip);
  }

  const animators = mapParticipantsToChips(resolveFacilitatorSource(session), (participant) => {
    return participant.role as ParticipantChip['role'];
  });

  if (session.type === 'welcome_meeting' && animators.length > 0) {
    setters.setSingleAnimatorId(animators[0].id);
    setters.setSingleAnimatorName(animators[0].name);
  } else {
    setters.setSelectedAnimators(animators);
  }

  const tutorParticipants = mapParticipantsToChips(
    session.type === 'information_meeting'
      ? session.participants?.filter((p) => roleKind(p.role) === 'tutor')
      : [],
    () => 'tutor',
  );
  setters.setSelectedTutorParticipants(tutorParticipants);

  const studentParticipants = mapParticipantsToChips(
    session.type === 'welcome_meeting'
      ? session.participants?.filter((p) => roleKind(p.role) === 'student')
      : [],
    () => 'student',
  );
  setters.setSelectedStudentParticipants(studentParticipants);

  if (session.managedBy) {
    setters.setManagedById(session.managedBy.id);
  }
  setters.setProgramId(session.programId ?? '');

  if (session.type === 'workshop') {
    setters.setEventType(session.eventType || '');
    setters.setLocation(session.location || '');
    if (session.budget) {
      setters.setBudgetPosts(session.budget.posts || []);
      setters.setEtpUnitCost(session.budget.unitCost || SESSION_REMOTE_FORM_DEFAULTS.etpUnitCost);
    }
  }
}

/** Validator context for create (aliases + `_sessionType`). */
export function buildCreateValidationContext(params: {
  selectedType: string;
  sessionDate: string;
  startTime: string;
  tutorId: string;
  studentId: string;
  managedById: string | null;
  recurrenceType: string;
  endTime: string;
  programId: string;
  eventType: string;
  sessionType: SessionTab;
  isPrefilledFromMatching: boolean;
  animatorsCount: number;
  singleAnimatorId: string;
}) {
  return {
    selectedType: params.selectedType,
    sessionDate: params.sessionDate,
    startTime: params.startTime,
    tutorId: params.tutorId,
    studentId: params.studentId,
    managedById: params.managedById,
    recurrenceType: params.recurrenceType,
    endTime: params.endTime,
    programId: params.programId,
    eventType: params.eventType,
    _sessionType: params.sessionType,
    _isPrefilledFromMatching: params.isPrefilledFromMatching,
    _animatorsCount: params.animatorsCount,
    _singleAnimatorId: params.singleAnimatorId,
  };
}

/** Validator context for edit. */
export function buildEditValidationContext(params: {
  sessionDate: string;
  startTime: string;
  managedById: string | null;
  programId: string;
  isTutoring: boolean;
}) {
  return {
    sessionDate: params.sessionDate,
    startTime: params.startTime,
    managedById: params.managedById,
    programId: params.programId,
    _isTutoring: params.isTutoring,
  };
}

/** Append `entry` when its id is not already in the list. */
export function appendUniqueById<T extends { id: string }>(list: T[], entry: T | undefined): T[] {
  if (!entry || list.some((item) => item.id === entry.id)) return list;
  return [...list, entry];
}

/** Drop the item with `id`. */
export function removeById<T extends { id: string }>(list: T[], id: string): T[] {
  return list.filter((item) => item.id !== id);
}

/** Current user as a {@link SelectedUser} chip. */
export function currentUserAsSelected(currentUser: {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
}): SelectedUser {
  return {
    id: currentUser.id,
    label: formatUserLabel(currentUser),
  };
}

/** Current user as an animator chip. */
export function currentUserAsAnimatorChip(currentUser: {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
}): ParticipantChip {
  return {
    id: currentUser.id,
    name: formatUserLabel(currentUser),
    role: 'recruiter',
  };
}

/** Merge tutor-situation rows by `userId` (later rows overwrite). */
export function mergeTutorSituationRecords<T extends { userId: string }>(
  awaitingStudent: T[],
  tutoringInProgress: T[],
): T[] {
  const byUserId = new Map(awaitingStudent.map((record) => [record.userId, record]));
  for (const record of tutoringInProgress) {
    if (!byUserId.has(record.userId)) byUserId.set(record.userId, record);
  }
  return [...byUserId.values()];
}

/** First id from a create response (single or array). */
export function firstCreatedSessionId(createdSession: unknown): string | undefined {
  const firstSession = Array.isArray(createdSession) ? createdSession[0] : createdSession;
  return firstSession?.id;
}

/** Immutable patch of one budget-post field. */
export function patchBudgetPost(
  posts: BudgetPost[],
  index: number,
  patch: Partial<BudgetPost>,
): BudgetPost[] {
  return posts.map((p, i) => (i === index ? { ...p, ...patch } : p));
}

/** Empty budget post (zero amount). */
export function emptyBudgetPost(): BudgetPost {
  return { type: SESSION_REMOTE_FORM_DEFAULTS.defaultBudgetPostType, amount: 0 };
}

/** True when information-meeting participant cap is reached. */
export function isInformationMeetingParticipantLimitReached(count: number): boolean {
  return count >= SESSION_REMOTE_FORM_DEFAULTS.maxParticipants;
}

/** True when the cancel reason requires a free-text comment. */
export function cancellationRequiresComment(reasonCode: string): boolean {
  return CANCELLATION_REASONS.find((r) => r.code === reasonCode)?.requiresComment ?? false;
}

/** Cancel API body from reason + comment. */
export function buildCancelPayload(
  reasonCode: CancellationReasonCode,
  reasonDetail: string,
): { reasonCode: CancellationReasonCode; reasonDetail?: string } {
  return {
    reasonCode,
    reasonDetail: cancellationRequiresComment(reasonCode) ? reasonDetail.trim() : undefined,
  };
}

/** Toast message from a cancel-session API error. */
export function cancelErrorMessage(err: unknown): string {
  return getErrorMessage(err);
}

/** Add/remove the current user from the selected-animator list. */
export function syncIncludeMeSelected(
  includeMe: boolean,
  currentUser:
    | {
        id: string;
        firstName?: string | null;
        lastName?: string | null;
        email?: string | null;
      }
    | null
    | undefined,
  selected: SelectedUser[],
): SelectedUser[] {
  if (!currentUser) return selected;
  if (includeMe && !selected.some((a) => a.id === currentUser.id)) {
    return [currentUserAsSelected(currentUser), ...selected];
  }
  if (!includeMe) {
    return selected.filter((a) => a.id !== currentUser.id);
  }
  return selected;
}

/** Add/remove the current user from animator chips. */
export function toggleIncludeMeAnimatorChips(
  includeMe: boolean,
  currentUser:
    | {
        id: string;
        firstName?: string | null;
        lastName?: string | null;
        email?: string | null;
      }
    | null
    | undefined,
  selected: ParticipantChip[],
): ParticipantChip[] {
  if (!currentUser) return selected;
  if (includeMe) {
    const meEntry = currentUserAsAnimatorChip(currentUser);
    return selected.some((a) => a.id === currentUser.id) ? selected : [meEntry, ...selected];
  }
  return selected.filter((a) => a.id !== currentUser.id);
}

/** Central error copy from {@link getErrorMessage}. */
export function sessionApiErrorMessage(error: unknown, _fallback?: string): string {
  return getErrorMessage(error);
}
